// docs/92 HM8: the huge foes' own words (content/huge/voices.json) and the moments they say them,
// shared by the scene (which sees the moments play) and the HUD (which shows the line). A line is
// picked by the fight, the unit, the moment and the pulse, so the same playback says the same.
import { create } from 'zustand';
import voices from '../../../../../content/huge/voices.json';
import type { HugeKind } from '../../game/hugeArt';

export type HugeSay = 'reveal' | 'move' | 'bloodied' | 'triumph' | 'fall';

interface KindVoice { title: string; eyebrow: string[]; reveal: string[]; move: string[]; bloodied: string[]; triumph: string[]; fall: string[] }

const BOOK = voices as unknown as Record<HugeKind, KindVoice>;

/** What the reveal card calls it ("A Red Dragon"). */
export function hugeTitle(kind: HugeKind): string { return BOOK[kind].title; }

function mix(parts: Array<number | bigint | string>): number {
  let h = 2166136261;
  for (const p of parts) for (const ch of String(p)) { h ^= ch.charCodeAt(0); h = Math.imul(h, 16777619) >>> 0; }
  return h >>> 0;
}

/** The reveal card's line over the name. */
export function hugeEyebrow(kind: HugeKind, seed: Array<number | bigint | string>): string {
  const pool = BOOK[kind].eyebrow;
  return pool[mix(['eyebrow', ...seed]) % pool.length]!;
}

/** One of its lines for `say`, `{name}` filled with the hero it is about. */
export function hugeLine(kind: HugeKind, say: HugeSay, seed: Array<number | bigint | string>, name?: string): string {
  const pool = BOOK[kind][say];
  const usable = name ? pool : pool.filter((l) => !l.includes('{name}'));
  const from = usable.length ? usable : pool;
  const line = from[mix([say, ...seed]) % from.length]!;
  return line.replace('{name}', name ?? 'that one');
}

/** A line the HUD shows over the board. */
export interface HugeBarkLine { key: string; unitId: number; name: string; kind: HugeKind; appearance: number; say: HugeSay; text: string; tile: { x: number; y: number }; size: number }

export interface HugeStore {
  bark?: HugeBarkLine;
  say(b: HugeBarkLine): void;
  clear(key: string): void;
  /** Fights × units already revealed this session (the reveal plays once; a reload checks localStorage too). */
  revealed: Set<string>;
}

export const useHuge = create<HugeStore>((set, get) => ({
  bark: undefined,
  // a reveal is never talked over by a move's line in the same moment
  say: (b) => { const cur = get().bark; if (cur && cur.say === 'reveal' && b.say === 'move' && cur.unitId === b.unitId) return; set({ bark: b }); },
  clear: (key) => { if (get().bark?.key === key) set({ bark: undefined }); },
  revealed: new Set(),
}));

/** Has this fight's huge unit been revealed to this browser (once per fight, docs/92 HM9)? */
export function revealedBefore(encounterId: number | bigint, unitId: number): boolean {
  const k = `${encounterId}:${unitId}`;
  if (useHuge.getState().revealed.has(k)) return true;
  try { return localStorage.getItem(`fk.huge.${k}`) === '1'; } catch { return false; }
}

export function markRevealed(encounterId: number | bigint, unitId: number): void {
  const k = `${encounterId}:${unitId}`;
  useHuge.getState().revealed.add(k);
  try { localStorage.setItem(`fk.huge.${k}`, '1'); } catch { /* private mode: once per session */ }
}
